/**
 * <tf-modal> — Modal premium con backdrop blur, fade+scale animation,
 * focus trap, ESC to close, click-outside to close.
 *
 * Sostituisce le N implementazioni inline di modal sparse nelle pagine.
 *
 * Uso HTML:
 *   <tf-modal id="my-modal" title="Conferma azione" size="md">
 *     <p>Sei sicuro di voler procedere?</p>
 *     <div slot="footer">
 *       <button data-tf-modal-close>Annulla</button>
 *       <button id="confirm-btn">Conferma</button>
 *     </div>
 *   </tf-modal>
 *
 * Uso JS:
 *   const modal = document.getElementById('my-modal') as TfModalElement;
 *   modal.open();
 *   modal.addEventListener('tf-modal-close', () => console.log('chiuso'));
 *   modal.close();
 *
 * Attributes:
 *   - title: string (default: "")
 *   - size: "sm" | "md" | "lg" | "xl" (default: "md")
 *   - closable: "true" | "false" (default: "true")
 *
 * Events:
 *   - tf-modal-open: dispatched dopo apertura
 *   - tf-modal-close: dispatched dopo chiusura
 *
 * @since v9.16 (Sprint 3)
 */

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export class TfModalElement extends HTMLElement {
  private _isOpen = false;
  private _previouslyFocused: HTMLElement | null = null;
  private _backdrop?: HTMLDivElement;
  private _container?: HTMLDivElement;

  static get observedAttributes(): string[] {
    return ['title', 'size', 'closable'];
  }

  connectedCallback(): void {
    this._render();
  }

  disconnectedCallback(): void {
    document.removeEventListener('keydown', this._onKeyDown);
  }

  attributeChangedCallback(): void {
    if (this._container) this._render();
  }

  // ============================================================
  // Public API
  // ============================================================

  open(): void {
    if (this._isOpen) return;
    this._isOpen = true;
    this._previouslyFocused = document.activeElement as HTMLElement;

    // Show backdrop + container
    if (this._backdrop) this._backdrop.classList.remove('hidden');
    if (this._container) {
      this._container.classList.remove('hidden');
      // Force reflow per animazione
      void this._container.offsetWidth;
      this._container.classList.add('tf-modal-visible');
    }

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    setTimeout(() => {
      const focusable = this.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus();
    }, 50);

    document.addEventListener('keydown', this._onKeyDown);

    this.dispatchEvent(new CustomEvent('tf-modal-open', { bubbles: true }));
  }

  close(): void {
    if (!this._isOpen) return;
    this._isOpen = false;

    if (this._container) this._container.classList.remove('tf-modal-visible');
    setTimeout(() => {
      if (this._backdrop) this._backdrop.classList.add('hidden');
      if (this._container) this._container.classList.add('hidden');
      document.body.style.overflow = '';

      this._previouslyFocused?.focus();
      document.removeEventListener('keydown', this._onKeyDown);

      this.dispatchEvent(new CustomEvent('tf-modal-close', { bubbles: true }));
    }, 200);
  }

  toggle(): void {
    this._isOpen ? this.close() : this.open();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  // ============================================================
  // Internal
  // ============================================================

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (!this._isOpen) return;
    const closable = this.getAttribute('closable') !== 'false';
    if (e.key === 'Escape' && closable) {
      e.preventDefault();
      this.close();
    }
  };

  private _render(): void {
    const title = this.getAttribute('title') || '';
    const size = (this.getAttribute('size') as keyof typeof SIZES) || 'md';
    const closable = this.getAttribute('closable') !== 'false';
    const sizeClass = SIZES[size] || SIZES.md;

    // Salva il contenuto custom (slot principale + slot=footer)
    const customContent = this.innerHTML;

    this.innerHTML = `
      <div class="tf-modal-backdrop fixed inset-0 z-[1000] hidden bg-slate-900/60 backdrop-blur-sm transition-opacity"></div>
      <div class="tf-modal-container fixed inset-0 z-[1001] hidden items-center justify-center p-4 transition-all duration-200" style="display:none;">
        <div class="tf-modal-panel relative w-full ${sizeClass} rounded-2xl bg-white shadow-2xl">
          ${
            title || closable
              ? `<div class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
                  <h3 class="text-lg font-semibold text-slate-900">${title}</h3>
                  ${
                    closable
                      ? `<button type="button" data-tf-modal-close class="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" aria-label="Chiudi">
                          <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>`
                      : ''
                  }
                </div>`
              : ''
          }
          <div class="tf-modal-body px-6 py-5">${customContent}</div>
        </div>
      </div>
    `;

    this._backdrop = this.querySelector<HTMLDivElement>('.tf-modal-backdrop')!;
    this._container = this.querySelector<HTMLDivElement>('.tf-modal-container')!;

    // Click backdrop → close (se closable)
    if (closable) {
      this._backdrop.addEventListener('click', () => this.close());
    }

    // Click button con [data-tf-modal-close] → close (delegated)
    this.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-tf-modal-close]')) {
        this.close();
      }
    });

    // Inject CSS animation styles (una sola volta nel document)
    if (!document.getElementById('tf-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'tf-modal-styles';
      style.textContent = `
        .tf-modal-container { display: none !important; }
        .tf-modal-container:not(.hidden) { display: flex !important; opacity: 0; }
        .tf-modal-container.tf-modal-visible { opacity: 1; }
        .tf-modal-panel { transform: scale(0.94); transition: transform 200ms cubic-bezier(0.4,0,0.2,1); }
        .tf-modal-container.tf-modal-visible .tf-modal-panel { transform: scale(1); }
        @media (prefers-reduced-motion: reduce) {
          .tf-modal-container, .tf-modal-panel { transition: none !important; }
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Auto-register al primo import
if (typeof customElements !== 'undefined' && !customElements.get('tf-modal')) {
  customElements.define('tf-modal', TfModalElement);
}
